// Funções de máscaras para formatação de inputs

// Máscara para valores monetários em reais
export const formatCurrency = (value: string | number): string => {
  if (value === null || value === undefined || value === '') return '';
  
  // Converter para número se for string
  const numericValue = typeof value === 'string' ? parseCurrency(value) : value;
  
  if (!numericValue || numericValue === 0) return '';
  
  // Formatar o valor corretamente
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  }).format(numericValue);
};

// Remove formatação de moeda e retorna número em centavos
export const parseCurrency = (value: string): number => {
  const numericValue = value.replace(/[^\d]/g, '');
  return numericValue ? parseInt(numericValue) : 0;
};

// Formatar valor considerando que o input são centavos
export const formatCurrencyFromCents = (cents: number): string => {
  if (!cents || cents === 0) return '';
  const reais = cents / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2
  }).format(reais);
};

// Máscara para telefone brasileiro e internacional
export const formatPhone = (value: string): string => {
  if (!value) return '';
  const raw = value.toString().trim();

  // Se for número internacional (começa com "+"), apenas normaliza mantendo dígitos
  if (raw.startsWith('+')) {
    const digits = raw.replace(/[^\d]/g, '');
    return `+${digits}`; // Mantém formato E.164 simples: +<digits>
  }

  // Remove tudo que não é dígito
  const numericValue = raw.replace(/\D/g, '');

  // Se tiver mais de 11 dígitos, provavelmente é internacional sem o '+' -> não aplicar máscara BR
  if (numericValue.length > 11) {
    return numericValue; // exibe puro para não distorcer
  }

  if (numericValue.length === 0) return '';
  if (numericValue.length <= 2) {
    return `(${numericValue}`;
  } else if (numericValue.length <= 7) {
    return `(${numericValue.slice(0, 2)}) ${numericValue.slice(2)}`;
  } else if (numericValue.length <= 10) {
    return `(${numericValue.slice(0, 2)}) ${numericValue.slice(2, 6)}-${numericValue.slice(6)}`;
  } else {
    return `(${numericValue.slice(0, 2)}) ${numericValue.slice(2, 7)}-${numericValue.slice(7, 11)}`;
  }
};

// Remove formatação de telefone, preservando '+' para números internacionais
export const parsePhone = (value: string): string => {
  if (!value) return '';
  const raw = value.toString().trim();
  const digits = raw.replace(/\D/g, '');
  return raw.startsWith('+') ? `+${digits}` : digits;
};

// Hook personalizado para input com máscara de moeda
export const useCurrencyInput = (initialValue: number = 0) => {
  const formatValue = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(value);
  };

  const handleChange = (value: string, onChange: (value: number) => void) => {
    const numericValue = parseCurrency(value);
    onChange(numericValue);
  };

  return {
    formatValue,
    handleChange,
    displayValue: formatValue(initialValue)
  };
};

// Hook personalizado para input com máscara de telefone
export const usePhoneInput = (initialValue: string = '') => {
  const handleChange = (value: string, onChange: (value: string) => void) => {
    onChange(parsePhone(value)); // Salva apenas números
  };

  return {
    handleChange,
    displayValue: formatPhone(initialValue)
  };
};

// Formatar CPF
export const formatCPF = (value: string): string => {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  return cleaned
    .replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2}).*/, '$1.$2.$3-$4')
    .replace(/(-$)/, '');
};

// Validar CPF
export const validateCPF = (cpf: string): boolean => {
  const cleaned = cpf.replace(/\D/g, '');
  
  if (cleaned.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;
  
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(9))) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(10))) return false;
  
  return true;
};

// Formatar CNPJ
export const formatCNPJ = (value: string): string => {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  return cleaned
    .replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2}).*/, '$1.$2.$3/$4-$5')
    .replace(/(-$)/, '');
};

// Formatar CPF/CNPJ automaticamente
export const formatCPFCNPJ = (value: string): string => {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  
  if (cleaned.length <= 11) {
    return formatCPF(cleaned);
  }
  
  return formatCNPJ(cleaned);
};

// Formatar RG
export const formatRG = (value: string): string => {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  return cleaned
    .replace(/^(\d{2})(\d{3})(\d{3})(\d{0,1}).*/, '$1.$2.$3-$4')
    .replace(/(-$)/, '');
};

// Formatar CEP
export const formatCEP = (value: string): string => {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  return cleaned
    .replace(/^(\d{5})(\d{0,3}).*/, '$1-$2')
    .replace(/(-$)/, '');
};

// Formatar data
export const formatDate = (value: string): string => {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  return cleaned
    .replace(/^(\d{2})(\d{2})(\d{0,4}).*/, '$1/$2/$3')
    .replace(/(\/$)/, '');
};